import { useMemo } from 'react';
import { Box, Typography } from '@shared/ui';
import { useSelector } from 'react-redux';
import { currencyFormat } from '@shared/ui/Table/lib/helpers';
import { selectCurrentPageContext } from '../model/selectors';
import MetricCard from './MetricCard';
import FilterStrip from './FilterStrip';

const FORMATTERS = {
  Number: 'Number',
  USD: 'USD',
  RoundingWithPercentages: 'RoundingWithPercentages',
  OneDecimal: 'OneDecimal',
  NoDecimal: 'NoDecimal',
};

// Backend aggregation key → display formatter. Keys come straight from
// the API response and must match the legacy mapping verbatim (1:1).
const FORMATTERS_BY_KEY = {
  totalCount: FORMATTERS.Number,
  TotalCount: FORMATTERS.Number,
  AccountsCount: FORMATTERS.Number,
  ViewedCount: FORMATTERS.Number,
  NotViewedCount: FORMATTERS.Number,
  PatientsCount: FORMATTERS.Number,
  totalAccountsCount: FORMATTERS.Number,
  EngagedCount: FORMATTERS.Number,
  uniqueAccounts: FORMATTERS.Number,
  Billed: FORMATTERS.Number,
  NotBilled: FORMATTERS.Number,
  Paid: FORMATTERS.Number,
  PR: FORMATTERS.Number,
  Denied: FORMATTERS.Number,
  Outstanding: FORMATTERS.Number,
  PartiallyPaid: FORMATTERS.Number,
  NotBillable: FORMATTERS.Number,
  AverageDaysToPayment: FORMATTERS.OneDecimal,
  AverageUnpaidAging: FORMATTERS.NoDecimal,
  completionRate: FORMATTERS.RoundingWithPercentages,
  noShowRate: FORMATTERS.RoundingWithPercentages,
  declinedRate: FORMATTERS.RoundingWithPercentages,
  noShowAndDeclinedRate: FORMATTERS.RoundingWithPercentages,
  performanceRate: FORMATTERS.RoundingWithPercentages,
  EngageRate: FORMATTERS.RoundingWithPercentages,
  TotalPaidAmount: FORMATTERS.USD,
  TotalPartiallyPaidAmount: FORMATTERS.USD,
  totalUnpaidAmount: FORMATTERS.USD,
  totalPaidAmount: FORMATTERS.USD,
};

const formatValue = (value, key) => {
  switch (FORMATTERS_BY_KEY[key]) {
    case FORMATTERS.USD:
      return currencyFormat(value);
    case FORMATTERS.RoundingWithPercentages:
      return `${(Math.round(value * 10000) / 100).toFixed(1)}%`;
    case FORMATTERS.OneDecimal:
      return Number(value).toFixed(1);
    case FORMATTERS.NoDecimal:
      return String(Math.round(Number(value)));
    default:
      return value;
  }
};

// Group metrics by their formatter family so the pane reads as a
// dashboard summary (counts, rates, amounts, averages) instead of a
// flat wall of numbers. Customer-review Improvement #6 — the old
// "big value stacked over eyebrow label" layout felt hero-heavy and
// hard to scan next to a busy table.
const CATEGORY_FOR = {
  [FORMATTERS.Number]: 'counts',
  [FORMATTERS.RoundingWithPercentages]: 'rates',
  [FORMATTERS.USD]: 'amounts',
  [FORMATTERS.OneDecimal]: 'averages',
  [FORMATTERS.NoDecimal]: 'averages',
};

const CATEGORY_ORDER = ['counts', 'rates', 'averages', 'amounts'];

const DEFAULT_CATEGORY_LABELS = {
  counts: 'Counts',
  rates: 'Response rates',
  averages: 'Averages',
  amounts: 'Amounts',
};

/**
 * InfoPane Metrics sub-page. Reads `aggregations`, `filter`, and `title`
 * from `currentPageContext` (passed in via `openInfoPane({ currentPageName: 'metrics', currentPageContext })`).
 * Renders a heading, FilterStrip, then metrics grouped into semantic
 * sections (Counts / Rates / Averages / Amounts) — unknown-formatter
 * keys fall into an "Other" bucket at the end so nothing is silently
 * dropped.
 *
 * Preserves the legacy FORMATTERS_BY_KEY mapping and the
 * `infoPane[title] + ' Metrics'` heading shape.
 */
const MetricsPane = () => {
  const context = useSelector(selectCurrentPageContext) || {};
  const { aggregations, filter = [], title } = context;
  const { tables, infoPane } = useSelector(state => state.i18n);

  const grouped = useMemo(() => {
    if (!aggregations) return [];

    const buckets = { counts: [], rates: [], averages: [], amounts: [], other: [] };
    Object.keys(aggregations).forEach(key => {
      const raw = aggregations[key];
      const formatter = FORMATTERS_BY_KEY[key];
      const category = CATEGORY_FOR[formatter] || 'other';
      buckets[category].push({
        key,
        label: tables[key] ?? key,
        value: formatValue(raw, key),
        // Rate rows also receive the raw 0..1 fraction so MetricCard
        // can draw the small progress bar under the row without
        // re-parsing the formatted string.
        ratePercent: category === 'rates' ? Number(raw) * 100 : undefined,
      });
    });

    return [...CATEGORY_ORDER, 'other']
      .filter(cat => buckets[cat].length > 0)
      .map(cat => ({
        id: cat,
        label: DEFAULT_CATEGORY_LABELS[cat] || null,
        rows: buckets[cat],
      }));
  }, [aggregations, tables]);

  return (
    <Box>
      <Box sx={{ padding: '18px 20px 6px' }}>
        <Typography
          sx={{
            fontFamily: 'var(--qhs-font-head)',
            fontSize: 22,
            fontWeight: 700,
            lineHeight: 1.2,
            letterSpacing: '-0.3px',
            // Without this the heading falls back to the MUI palette's
            // default text colour, which only knows the light theme —
            // black on the dark pane. Every other title in this widget
            // already names the token.
            color: 'var(--qhs-text-1)',
          }}
        >
          {`${infoPane[title] ?? title} ${infoPane.metrics}`}
        </Typography>
      </Box>
      <FilterStrip filter={filter} />
      {/* Keying the sections list on the context title remounts every
          MetricCard when the user switches between different metrics
          pages (e.g. Claims -> Assessments). That resets each card's
          flash mount guard, so overlapping label names (Total Count,
          Accounts Count) do not light up yellow on the page switch —
          flash now fires only on live value updates inside the same
          metrics context. */}
      <Box key={title} sx={{ padding: '4px 20px 24px' }}>
        {grouped.map(section => (
          <Box key={section.id} sx={{ mt: 2, '&:first-of-type': { mt: 0 } }}>
            {section.label && (
              <Typography
                sx={{
                  fontSize: 10.5,
                  fontWeight: 700,
                  letterSpacing: '0.8px',
                  textTransform: 'uppercase',
                  color: 'var(--qhs-text-3)',
                  mt: 1.5,
                  mb: 0.5,
                }}
              >
                {section.label}
              </Typography>
            )}
            {section.rows.map(row => (
              <MetricCard
                key={row.key}
                label={row.label}
                value={row.value}
                ratePercent={row.ratePercent}
              />
            ))}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default MetricsPane;
